import React, { useState, useEffect } from "react";

interface DollarInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
}

const DollarInput: React.FC<DollarInputProps> = ({
  value,
  onChange,
  placeholder = "$0.00",
  label,
  required = false,
  disabled = false,
  className = "",
  id,
  "aria-describedby": ariaDescribedby,
}) => {
  const [displayValue, setDisplayValue] = useState("");

  // Format the display value with proper dollar formatting
  const formatDisplayValue = (numValue: number): string => {
    if (numValue === 0) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  // Update display value when the actual value changes
  useEffect(() => {
    setDisplayValue(formatDisplayValue(value));
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty input
    if (inputValue === "") {
      setDisplayValue("");
      onChange(0);
      return;
    }

    // Remove all non-numeric characters except decimal point
    const cleanValue = inputValue.replace(/[^0-9.]/g, "");
    
    // Handle multiple decimal points (keep only the first one)
    const parts = cleanValue.split(".");
    if (parts.length > 2) {
      const firstPart = parts[0];
      const rest = parts.slice(1).join("");
      const cleanedValue = `${firstPart}.${rest}`;
      const numValue = parseFloat(cleanedValue) || 0;
      setDisplayValue(formatDisplayValue(numValue));
      onChange(numValue);
      return;
    }

    // Parse the numeric value
    const numValue = parseFloat(cleanValue) || 0;
    
    // Format for display
    const formattedValue = formatDisplayValue(numValue);
    setDisplayValue(formattedValue);
    
    // Update the actual value
    onChange(numValue);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text when focusing on empty input
    if (value === 0) {
      e.target.select();
    }
  };

  const handleBlur = () => {
    // Ensure proper formatting when leaving the input
    setDisplayValue(formatDisplayValue(value));
  };

  return (
    <div className={className}>
      {label && (
        <label 
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {label}
        </label>
      )}
      <input
        id={id}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="input w-full"
        aria-describedby={ariaDescribedby}
        inputMode="decimal"
        autoComplete="off"
      />
    </div>
  );
};

export default DollarInput; 